import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import {
  DataBundleProduct,
  DataBundlePurchaseResponse,
  DataBundleService,
} from "../app_utils/api/DataBundleService";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { UserSessionUtils } from "../app_utils/utils/UserSessionUtils";

const resolveNetworkCode = (telcoName: string): string | null => {
  const upper = (telcoName || "").toUpperCase();
  if (upper.includes("MTN")) return "MTN";
  if (upper.includes("AIRTEL")) return "AIRTEL";
  if (upper.includes("LYCA")) return "LYCA";
  return null;
};

const DataBundles: React.FC = () => {
  const [telcos, setTelcos] = useState<{ telco: string; bundles: DataBundleProduct[] }[]>([]);
  const [selectedTelco, setSelectedTelco] = useState<string | null>(null);
  const [bundles, setBundles] = useState<DataBundleProduct[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<DataBundleProduct | null>(null);
  const [recipients, setRecipients] = useState<string>("");
  const [isPurchasing, setIsPurchasing] = useState<boolean>(false);
  const [isLoadingCatalogue, setIsLoadingCatalogue] = useState<boolean>(false);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [purchaseResult, setPurchaseResult] = useState<DataBundlePurchaseResponse | null>(null);
  const [isResultDialogVisible, setResultDialogVisible] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const userId = UserSessionUtils.getUserDetails()?.id;

  useEffect(() => {
    setIsLoadingCatalogue(true);
    DataBundleService.getCatalogue()
      .then((data) => {
        setTelcos(data);
        setIsLoadingCatalogue(false);
      })
      .catch((error) => {
        setIsLoadingCatalogue(false);
        toast.current?.show({
          severity: "error",
          summary: "Catalogue Error",
          detail: error.message,
        });
      });
  }, []);

  useEffect(() => {
    if (!userId) return;
    new BaseApiServiceImpl("/api/v1/wallet-deposits/balance-info/" + userId)
      .getRequestWithJsonResponse({})
      .then((response) => {
        setWalletBalance(response?.data?.balance ?? 0);
      })
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    const telco = telcos.find((item) => item.telco === selectedTelco);
    setBundles(telco ? telco.bundles : []);
    setSelectedBundle(null);
  }, [selectedTelco, telcos]);

  const parseRecipients = (): string[] => {
    return recipients
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const recipientCount = parseRecipients().length;
  const totalCost = selectedBundle && recipientCount > 0 ? selectedBundle.amount * recipientCount : 0;
  const insufficientFunds = totalCost > walletBalance;

  const validateForm = (): boolean => {
    if (!selectedTelco) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please select a network.",
      });
      return false;
    }
    if (!selectedBundle) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please select a data bundle.",
      });
      return false;
    }
    const numbers = parseRecipients();
    if (numbers.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter at least one recipient phone number.",
      });
      return false;
    }
    if (numbers.length > 200) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "A maximum of 200 recipients is allowed per purchase.",
      });
      return false;
    }
    const invalidNumbers = numbers.filter((num) => !/^\+?[1-9]\d{6,14}$/.test(num.replace(/\s/g, "")));
    if (invalidNumbers.length > 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: `Invalid phone number(s): ${invalidNumbers.join(", ")}. Use format 256755170341 or +256755170341.`,
      });
      return false;
    }
    if (insufficientFunds) {
      toast.current?.show({
        severity: "error",
        summary: "Insufficient Balance",
        detail: `This purchase costs ${totalCost.toFixed(2)} ${selectedBundle.currency} but your balance is ${walletBalance.toFixed(2)}. Please top up your wallet.`,
      });
      return false;
    }
    return true;
  };

  const doPurchase = async () => {
    if (!validateForm()) return;

    const networkCode = resolveNetworkCode(selectedTelco || "");
    setIsPurchasing(true);
    try {
      const response = await DataBundleService.purchaseBundles({
        Currency: selectedBundle?.currency,
        recipients: parseRecipients().map((msisdn) => ({
          msisdn: msisdn.startsWith("+") ? msisdn : `+${msisdn}`,
          network: networkCode || "",
          quantity: selectedBundle!.quantity,
          unit: selectedBundle!.unit,
          validity: selectedBundle!.validity,
        })),
      });
      setIsPurchasing(false);
      setPurchaseResult(response);
      setResultDialogVisible(true);
      setRecipients("");
    } catch (error: any) {
      setIsPurchasing(false);
      toast.current?.show({
        severity: "error",
        summary: "Purchase Failed",
        detail: error.message,
      });
    }
  };

  const telcoOptions = telcos.map((item) => ({ label: item.telco, value: item.telco }));
  const bundleOptions = bundles.map((bundle) => ({
    label: `${bundle.label} (${bundle.quantity}${bundle.unit}, ${bundle.validity}) - ${bundle.currency} ${bundle.amount}`,
    value: bundle.id,
  }));

  return (
    <div className="p-m-4 card">
      <h4 className="p-text-center">Buy Data Bundles</h4>
      <div className="grid">
        <div className="col-12 md:col-8 grid">
          <div className="col-12 md:col-6">
            <label htmlFor="network" className="w-full p-d-block">
              Network
            </label>
            <Dropdown
              id="network"
              value={selectedTelco}
              options={telcoOptions}
              onChange={(e) => setSelectedTelco(e.value)}
              placeholder={isLoadingCatalogue ? "Loading catalogue..." : "Select network"}
              className="w-full mt-1"
              disabled={isLoadingCatalogue || isPurchasing}
            />
          </div>
          <div className="col-12 md:col-6">
            <label htmlFor="bundle" className="w-full p-d-block">
              Data Bundle
            </label>
            <Dropdown
              id="bundle"
              value={selectedBundle?.id}
              options={bundleOptions}
              onChange={(e) =>
                setSelectedBundle(bundles.find((bundle) => bundle.id === e.value) || null)
              }
              placeholder="Select bundle"
              className="w-full mt-1"
              disabled={!selectedTelco || isPurchasing}
            />
          </div>

          <div className="col-12 mt-2">
            <label htmlFor="recipients" className="w-full p-d-block">
              Recipient Phone Number(s)
            </label>
            <InputTextarea
              id="recipients"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="Enter phone numbers separated by comma or new line e.g 256701609927,+256701609927"
              className="w-full mt-1"
              rows={5}
              disabled={isPurchasing}
            />
            <small className="text-500">{recipientCount} recipient(s), maximum 200</small>
          </div>

          <div className="col-12 md:col-4 ml-1 grid mt-3">
            <Button
              loading={isPurchasing}
              label="Buy Bundle(s)"
              icon="pi pi-shopping-cart"
              className="p-button-success col-12"
              onClick={doPurchase}
              disabled={insufficientFunds}
            />
          </div>
        </div>

        <div className="col-12 md:col-4">
          <div className="card" style={{ background: "#f4f6f9" }}>
            <h5 className="mb-3">Cost Estimation</h5>
            <div className="mb-2">
              <span className="text-500">Wallet Balance:</span>
              <span className="font-bold ml-2">{walletBalance.toFixed(2)} UGX</span>
            </div>
            <div className="mb-2">
              <span className="text-500">Bundle Price:</span>
              <span className="font-bold ml-2">
                {selectedBundle ? `${selectedBundle.currency} ${selectedBundle.amount}` : "-"}
              </span>
            </div>
            <div className="mb-2">
              <span className="text-500">Recipients:</span>
              <span className="font-bold ml-2">{recipientCount}</span>
            </div>
            <div className="mb-2">
              <span className="text-500">Total Cost:</span>
              <span className="font-bold ml-2">
                {selectedBundle ? `${selectedBundle.currency} ${totalCost.toFixed(2)}` : "-"}
              </span>
            </div>
            <ProgressBar
              value={Math.min((walletBalance / Math.max(totalCost, 1)) * 100, 100)}
              style={{ height: "8px" }}
              className={insufficientFunds ? "p-progressbar-danger" : "p-progressbar-success"}
            />
            {insufficientFunds && (
              <Button
                label="Top Up Wallet"
                icon="pi pi-wallet"
                className="p-button-warning w-full mt-3"
                onClick={() => {
                  window.location.href = "/wallet";
                }}
              />
            )}
          </div>

          <div className="mt-3">
            <h3>Guidance</h3>
            <p>
              <strong>Step 1:</strong> Select the network and browse available data bundles.
            </p>
            <p>
              <strong>Step 2:</strong> Choose the bundle you want to send (size, unit and validity are shown).
            </p>
            <p>
              <strong>Step 3:</strong> Enter up to 200 recipient numbers separated by comma or new line.
            </p>
            <p>
              <strong>Step 4:</strong> Buy the bundles and track progress in Data Bundle Reports.
            </p>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
      <Dialog
        header="Purchase Submitted"
        visible={isResultDialogVisible}
        style={{ width: "35vw" }}
        onHide={() => setResultDialogVisible(false)}
      >
        {purchaseResult && (
          <div>
            <p>
              <strong>Status:</strong> {purchaseResult.status}
            </p>
            <p>
              <strong>Reference:</strong> {purchaseResult.kacyberReference}
            </p>
            <p>
              <strong>Your Reference:</strong> {purchaseResult.externalReferenceID}
            </p>
            <p>
              <strong>Sent:</strong> {purchaseResult.totalSent} |{" "}
              <strong>Failed:</strong> {purchaseResult.totalFailed}
            </p>
            <p>
              <strong>Total Amount:</strong> {purchaseResult.totalAmount}
            </p>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default DataBundles;
